import { useApiFetch } from '~/composables/useApiFetch';
import type {
  ChangeOrganizationResponse,
  DeleteOrganizationResponse,
  JoinOrgRequest,
  Organization,
  OrganizationsMember,
  TransferOwnershipRequest,
} from '~/types/organization';
import { unwrapEnvelope } from '~/utils/apiEnvelope';

export async function fetchCurrentOrganization(): Promise<OrganizationsMember> {
  const response = await useApiFetch('/organizations/current', {
    method: 'GET',
    requireAuth: true,
  });
  return unwrapEnvelope<OrganizationsMember>(await response.json());
}

export async function changeOrganizationApi(
  organizationUuid: string
): Promise<ChangeOrganizationResponse> {
  const response = await useApiFetch(`/organizations/change/current?uuid=${organizationUuid}`, {
    method: 'POST',
    requireAuth: true,
  });
  return unwrapEnvelope<ChangeOrganizationResponse>(await response.json());
}

export async function joinOrgApi(organizationUuid: string): Promise<boolean> {
  const requestBody: JoinOrgRequest = {
    organization_uuid: organizationUuid,
  };
  const response = await useApiFetch('/organizations/member/join', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify(requestBody),
  });
  return unwrapEnvelope<boolean>(await response.json());
}

export async function createOrgApi(name: string): Promise<OrganizationsMember> {
  const response = await useApiFetch('/organizations', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ name }),
  });
  return unwrapEnvelope<OrganizationsMember>(await response.json());
}

export async function inviteMemberApi(email: string): Promise<void> {
  await useApiFetch('/organizations/member/invite', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ email }),
  });
}

export async function inviteMembersApi(emails: string[]): Promise<void> {
  await useApiFetch('/organizations/member/invite', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ emails }),
  });
}

export async function getOrganizationsListApi(): Promise<Organization[]> {
  const response = await useApiFetch('/organizations/list', {
    method: 'GET',
    requireAuth: true,
  });
  return unwrapEnvelope<Organization[]>(await response.json());
}

export async function leaveOrgApi(organizationUuid: string): Promise<ChangeOrganizationResponse> {
  const response = await useApiFetch('/organizations/member/leave', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
  return unwrapEnvelope<ChangeOrganizationResponse>(await response.json());
}

export async function getOrganizationMembersApi(): Promise<OrganizationsMember[]> {
  const response = await useApiFetch('/organizations/members', {
    method: 'GET',
    requireAuth: true,
  });
  return unwrapEnvelope<OrganizationsMember[]>(await response.json());
}

export async function updateOrganizationNameApi(
  organizationUuid: string,
  name: string
): Promise<void> {
  await useApiFetch('/organizations', {
    method: 'PUT',
    requireAuth: true,
    body: JSON.stringify({ organizations_uuid: organizationUuid, name: name }),
  });
}

export async function deleteMemberApi(params: { uid: number; id: number }): Promise<void> {
  const { uid, id } = params;
  const query = uid && uid !== 0 ? `uid=${uid}` : `id=${id}`;

  await useApiFetch(`/organizations/member/remove?${query}`, {
    method: 'DELETE',
    requireAuth: true,
  });
}

export async function declineOrganizationApi(organizationUuid: string): Promise<boolean> {
  const response = await useApiFetch('/organizations/member/reject', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
  return unwrapEnvelope<boolean>(await response.json());
}

export async function acceptOrganizationApi(organizationToken: string): Promise<boolean> {
  const response = await useApiFetch(`/organizations/member?access_token=${organizationToken}`, {
    method: 'GET',
    requireAuth: true,
  });
  return unwrapEnvelope<boolean>(await response.json());
}

/**
 * 邀請方軟刪除邀請
 * @param organizationUuid 組織 UUID
 * @param targetUid 被邀請人的 UID
 */
export async function softDeleteInviteByInviterApi(
  organizationUuid: string,
  targetUid: number
): Promise<void> {
  await useApiFetch('/organizations/member/invite/delete', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({
      organization_uuid: organizationUuid,
      target_uid: targetUid,
    }),
  });
}

/**
 * 被邀請方軟刪除邀請
 * @param organizationUuid 組織 UUID
 */
export async function softDeleteInviteByInviteeApi(organizationUuid: string): Promise<void> {
  await useApiFetch('/organizations/member/invite/dismiss', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
}

/**
 * 轉移組織擁有權（僅 owner 可呼叫）。
 * 成功後原 owner 降為普通成員，target 升為 owner。此端點不重發 token —— 權限
 * scope 變了，呼叫端必須自行換一張。
 *
 * handleError: false —— 拒絕面（非 owner／轉給自己／對象未接受邀請）後端回
 * 422 + 已翻譯的 message，交呼叫端顯示，不走全域 toast。
 */
export async function transferOwnershipApi(
  organizationUuid: string,
  targetUserId: number
): Promise<void> {
  const requestBody: TransferOwnershipRequest = {
    organization_uuid: organizationUuid,
    target_user_id: targetUserId,
  };
  await useApiFetch('/organizations/owner/transfer', {
    method: 'POST',
    requireAuth: true,
    handleError: false,
    body: JSON.stringify(requestBody),
  });
}

/**
 * 刪除組織（僅 owner，軟刪除）。
 * 後端前置條件：組織只剩 owner 一人。餘額檢查已移除，有餘額也刪得掉，
 * 錢包行保留但不退款（BE Momentx-AI-Business#877）。
 *
 * 實查餘額 > 0 時 `acknowledged_balance` 必填且須與實查值一致，否則 409
 * `balance_ack_required`（BE #883）—— 知情確認由伺服器端驗，純前端警語擋不住
 * stale client。餘額為 0 時不帶照常刪。
 *
 * 成功後 current 由後端切到剩餘組織（或清空），回應與 leave 同契約帶新 token；
 * 後端重發 token 失敗時改回 { deleted, refresh_needed }。
 */
export async function deleteOrganizationApi(
  organizationUuid: string,
  acknowledgedBalance?: number
): Promise<DeleteOrganizationResponse> {
  // 只有拿得到的有效數字才送。查不到餘額時寧可不帶，讓後端用 409 把實值告訴我們，
  // 也不要送一個猜來的數字去撞「須與實查值一致」。
  const acknowledge =
    typeof acknowledgedBalance === 'number' && Number.isFinite(acknowledgedBalance)
      ? `&acknowledged_balance=${encodeURIComponent(String(acknowledgedBalance))}`
      : '';
  const response = await useApiFetch(`/organizations?uuid=${organizationUuid}${acknowledge}`, {
    method: 'DELETE',
    requireAuth: true,
    handleError: false,
  });
  return unwrapEnvelope<DeleteOrganizationResponse>(await response.json());
}
